<template>
  <div class="input-text input-group text-left">
    <hl-ErrorMessage v-if="error" :errorVariant="errorVariant">{{ error }}</hl-ErrorMessage>

    <div class="input-group" :class="{ 'input-group-sm': size === 'sm' }">
      <div v-if="showPrepend" class="input-group-prepend">
        <span class="input-group-text" id="inputGroupPrepend">
          {{ prependContent }}
        </span>
      </div>

      <input
        :type="inputType"
        :value="value"
        :placeholder="placeholder"
        @input="updateValue"
        :disabled="disabled"
        class="form-control"
        :class="[
          getErrorClass,
          textAlignClass,
          {
            'input-append': Boolean(postfix),
            'text-right': isTextRightAligned,
          },
        ]"
        :autofocus="autofocus"
        :name="nameOrLabel"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        :data-vv-as="label ? label : nameForValidation"
        data-vv-delay="1000"
        :data-vv-scope="vvScope"
        :autocomplete="autocomplete"
        :readonly="readonly"
        :data-cy="dataCy"
        ref="input"
      />

      <div v-if="postfix" class="input-group-append">
        <span class="input-group-append-text" id="inputGroupPrepend">
          {{ postfix }}
        </span>
      </div>
    </div>

    <a-Label v-if="label" :optional="optional">
      {{ label }}
    </a-Label>
  </div>
</template>

<script>
import ErrorMessage from '@modules/husleie-framework/src/components/text/ErrorMessage.vue';
import Label from '@modules/design-system/components/atoms/labels/Label.vue';

export default {
  components: {
    'a-Label': Label,
    'hl-ErrorMessage': ErrorMessage,
  },
  props: {
    autocomplete: String,
    autofocus: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
    },
    errorVariant: {
      type: String,
      default: 'danger',
    },
    name: String,
    value: [String, Number],
    label: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'lg',
    },
    vvScope: {
      type: String,
      default: undefined,
    },
    nameForValidation: {
      type: String,
    },
    showPrepend: {
      type: Boolean,
      default: false,
    },
    prependContent: {
      type: [String, Number],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
      validator: val => {
        return ['url', 'text', 'password', 'email', 'search', 'number', 'date', 'tel', 'time'].indexOf(val) !== -1;
      },
    },
    postfix: {
      type: String,
      required: false,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    dataCy: {
      type: String,
      required: false,
    },
    isTextRightAligned: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    updateValue(e) {
      if (this.isNumberInput) {
        let inputValue = e.target.value;
        inputValue = this.filterNumberInput(inputValue);

        // Update the input field to show filtered value
        e.target.value = inputValue;
      }
      this.$emit('input', e.target.value);
    },

    focus() {
      this.$refs.input.focus();
    },


    filterNumberInput(value) {
      // Allow only digits and one comma or dot
      let filtered = value
        .replace(/[^0-9.,-]/g, '')
        .replace(',', '.')
        .replace(/(,.*),/g, '$1')
        .replace(/(\..*)\./g, '$1')
        .replace(/(-.*)-/g, '$1')

      return filtered;
    },
  },
  computed: {
    /*
    inputFlags() {
      return this._hl_fields[this.name] || {};
    },
    isValidated() {
      if (this.name in this._hl_fields) {
        return this._hl_fields[this.name].validated;
      }
    },
 */
    textAlignClass() {
      return this.type === 'number' ? 'text-right' : 'text-left';
    },

    nameOrLabel() {
      if (this.name) return this.name;

      if (this.label) return encodeURIComponent(this.label).toLowerCase();

      return 'placeholder' + Math.round(Math.random() * 100000);
    },

    getErrorClass() {
      const classes = ['input'];

      if (this.error && this.errorVariant === 'danger') {
        classes.push('is-invalid');
      }

      if (this.error && this.errorVariant === 'warning') {
        classes.push('is-warning');
      }
      return classes;
    },

    inputType() {
      return this.isNumberInput ? 'text' : this.type;
    },
    
    isNumberInput() {
      return this.type === 'number';
    },
  },
};
</script>

<style scoped lang="scss">
.form-control.is-warning {
  border-color: $warning;
}

.input-text {
  display: flex;
  flex-direction: column-reverse;
  position: relative;

  p {
    margin-top: 1rem;
  }

  label {
    font-size: 0.8em;
    color: $husleie-text-light;
  }

  input {
    &:focus {
      + label {
        color: $husleie-text-medium;
      }
    }
  }
}

.has-icon {
  padding-right: 50px;
}

.input-icon {
  background-color: $primary;
  background-image: url('../../../../../icons/custom/checkmark.svg');
  background-size: 12px;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  color: #fff;
  position: absolute;
  right: 0.8rem;
  z-index: 2;
  display: block;
  width: 22px;
  height: 22px;
  line-height: 34px;
  text-align: center;
  pointer-events: none;
}

.has-label {
  top: 2.6rem;
}

.no-label {
  top: 1rem;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.input-group-append-text {
  display: flex;
  align-items: center;
  padding: 0.8rem 0.8rem 0.8rem 0;
  margin-bottom: 0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #0a1f44;
  text-align: center;
  white-space: nowrap;
  border: 1px solid #ced4da;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  border-left: 0;
}

.input-append {
  border-right: 0;
  padding-right: 0.4rem;
}
</style>
